import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error(t("reset.mismatch"));
    if (password.length < 8) return toast.error(t("reset.tooShort"));
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("reset.updated"));
      navigate("/");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("reset.failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9] p-6">
      <div className="max-w-md w-full bg-white border border-[#EBEBEA] rounded-2xl p-10">
        <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-4">{t("auth.reset")}</p>
        <h1 className="font-display text-3xl font-semibold text-[#0A110E] mb-2">{t("reset.title")}</h1>
        <p className="text-sm text-[#5F7368] mb-8">
          {t("reset.desc")}
        </p>
        {!token ? (
          <p className="text-sm text-[#D95D39]">{t("reset.invalid")} <Link to="/" className="underline">{t("reset.goHome")}</Link>.</p>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <input
              type="password"
              required
              minLength={8}
              placeholder={t("reset.newPassword")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input"
              className="px-4 py-3 rounded-xl border border-[#EBEBEA] bg-white focus:outline-none focus:ring-2 focus:ring-[#1C7C54]/30"
            />
            <input
              type="password"
              required
              minLength={8}
              placeholder={t("reset.repeatPassword")}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-password-confirm"
              className="px-4 py-3 rounded-xl border border-[#EBEBEA] bg-white focus:outline-none focus:ring-2 focus:ring-[#1C7C54]/30"
            />
            <button
              type="submit"
              disabled={saving}
              data-testid="reset-submit-btn"
              className="mt-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#0F291E] text-white font-semibold hover:bg-[#1C7C54] transition-colors disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("reset.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
